/*
 * Every path that comes from outside — a `next=` parameter, a deep link handed over by the
 * platform, the route remembered from the last visit — passes through here before anything
 * navigates to it. It is the client half of the product's same-origin check (REUSE A6): a value
 * that is not a path *within this origin* is refused rather than repaired, because "repaired" is
 * how an open redirect gets built.
 */

import {
	readSessionValue,
	readValue,
	storageKey,
	writeSessionValue,
	writeValue,
} from "../lib/storage.ts"

const LAST_ROUTE_KEY = storageKey("lastRoute")

/** True while the string carries a control character, which a URL never legitimately does. */
function hasControlCharacter(raw: string): boolean {
	for (const character of raw) {
		const code = character.codePointAt(0) ?? 0
		if (code < 0x20 || code === 0x7f) return true
	}
	return false
}

/**
 * The path itself when it is a same-origin path, else null. Rejected: an absolute URL of any
 * scheme, a protocol-relative "//host", a backslash (some parsers read it as a slash, so
 * "/\evil.example" would leave the origin) and any control character.
 */
export function safePath(raw: string): string | null {
	if (!raw.startsWith("/")) return null
	if (raw.startsWith("//")) return null
	if (raw.includes("\\")) return null
	if (hasControlCharacter(raw)) return null
	return raw
}

/** The routes the reader is *in* rather than passing through: the two kinds of conversation. */
const CONVERSATION_PREFIXES = ["/c/", "/d/"]

/**
 * Remembers the conversation the reader was in, so that Home opens it again (UX §1.3). Only a
 * conversation is remembered: this value is what `/` redirects to, and remembering every path made
 * Home land on the People directory or, worse, on itself (A-06 D4).
 *
 * A permalink is not remembered either — coming back should open the conversation at its newest
 * end, not at the message somebody once linked — and neither is a search string, which is where an
 * open thread writes itself.
 */
export function rememberRoute(path: string): void {
	const safe = safePath(path)
	if (safe === null) return
	const pathname = safe.split("?")[0] ?? ""
	if (!CONVERSATION_PREFIXES.some((prefix) => pathname.startsWith(prefix))) return
	if (pathname.split("/").includes("p")) return
	writeValue(LAST_ROUTE_KEY, pathname)
}

/** The remembered conversation, or null when there is none this build would navigate to. */
export function lastRoute(): string | null {
	return safePath(readValue(LAST_ROUTE_KEY))
}

/*
 * Whether this tab has already been somewhere. The remembered conversation is where a *cold start*
 * lands (G-52): a person who opens the application at "/" is put back where they were. Once the tab
 * has shown any other address, "/" is the rail's *Home* — somebody asking for Home, and not for the
 * conversation they just left — so the landing redirect happens once per tab and never again.
 *
 * It is remembered for the tab and not for the device: a second tab is a second cold start, and
 * closing the browser makes the next visit one again.
 */
const LANDED_KEY = "tbLanded"

/** Notes the address the shell is showing; every one but "/" ends the cold start. */
export function noteRouteShown(pathname: string): void {
	if (pathname === "/") return
	endColdStart()
}

/**
 * Ends the cold start without an address having been shown. Home calls it while it is *greeting*
 * somebody (X-A4 N15): the greeting is what this visit landed on, so dismissing it must leave the
 * reader on Home rather than letting the landing redirect fire a second later and take them into a
 * conversation they did not ask for.
 */
export function endColdStart(): void {
	writeSessionValue(LANDED_KEY, "1")
}

/** True while this tab has shown nothing but "/", which is the one landing that follows the reader. */
export function isColdStart(): boolean {
	return readSessionValue(LANDED_KEY) === ""
}

/*
 * The application's URL space. A module registers the routes, and this layer knows their shape:
 * that is what a routes layer is, and it is why a screen that has to turn "conversation 7,
 * message 12" into a link asks here rather than growing an opinion of its own about `/c/`.
 */

/** Enough of a conversation to address it. A `ConversationView` of any layer satisfies this. */
export interface Addressable {
	kind: string
	/** A channel and a private conversation carry one; a direct conversation does not. */
	slug?: string | undefined
	/** A direct conversation is addressed by its generated handle ("anna+bert") instead. */
	dmHandle?: string | undefined
}

/** Where a conversation lives, or null when it carries neither address. */
export function conversationHref(conv: Addressable): string | null {
	if (conv.kind === "dm" || conv.kind === "groupDm") {
		return conv.dmHandle === undefined || conv.dmHandle === ""
			? null
			: `/d/${encodeURIComponent(conv.dmHandle)}`
	}
	return conv.slug === undefined || conv.slug === "" ? null : `/c/${encodeURIComponent(conv.slug)}`
}

/**
 * The permalink of one message: the conversation opened around that message rather than at its
 * newest end, which is what makes a search hit and an Activity row land in their context. Both
 * kinds of conversation have one — `/c/<slug>/p/<seq>` and `/d/<handle>/p/<seq>` — so a message is
 * addressed the same way wherever it was written.
 */
export function messageHref(conv: Addressable, seq: number): string | null {
	const base = conversationHref(conv)
	return base === null ? null : `${base}/p/${seq}`
}

/**
 * Where a code search hit opens: `/code/<repo>/<path within it>?line=<n>&at=<commit>`, the code file
 * page (`core/search/CodeFilePage.tsx`). The repository and the path are the file's address; the
 * line is where the hit stands and the commit is the version the search read, so the marked line is
 * the one that matched.
 *
 * Every segment is encoded on its own, so the slashes of the path stay slashes and a space, a `#`
 * or a `?` in a file name cannot end the path or start a query. A path is stored as git stores it,
 * which allows both, and `decodeURIComponent` on the other side gives the bytes back.
 *
 * No matched text travels in it: an address reaches the browser history, the `Referer` of the next
 * outbound click and every access log in front of the instance, and what somebody searched for is
 * theirs. Line numbers and commits say nothing about it.
 */
export function codeFileHref(repo: string, path: string, line: number | null, at: string): string {
	const within = path
		.split("/")
		.filter((segment) => segment !== "")
		.map(encodeURIComponent)
		.join("/")
	const q = new URLSearchParams()
	if (line !== null) q.set("line", String(line))
	if (at !== "") q.set("at", at)
	const query = q.toString()
	return `/code/${encodeURIComponent(repo)}${within === "" ? "" : `/${within}`}${query === "" ? "" : `?${query}`}`
}

/**
 * The AI chats place: the rail's row, and the head of every AI chat's address (`/ai/<key>`). The
 * module's own doors build on it, so the row and every link to a chat are one address written once.
 */
export const AI_PLACE = "/ai"

/**
 * The drive's place: the rail's row and the head of every drive address (`/drive/<key>`), the same
 * prefix the drive's wire names (`PathPrefix`) and the document page reads (`DOCUMENT_PATH_PREFIX`).
 */
export const DRIVE_PLACE = "/drive"

/** An AI chat's page, with the page's own search parameters where a door names a row or a panel. */
export function aiChatHref(key: string, search?: Record<string, string | number>): string {
	const q = new URLSearchParams(
		Object.entries(search ?? {}).map(([name, value]) => [name, String(value)]),
	).toString()
	return `${AI_PLACE}/${encodeURIComponent(key)}${q === "" ? "" : `?${q}`}`
}

/** An AI chat's review. */
export function aiChangesHref(key: string): string {
	return `${AI_PLACE}/${encodeURIComponent(key)}/changes`
}

/** The new-chat page, prefilled from another chat where `from` names one. */
export function aiNewHref(from?: string): string {
	return from === undefined ? `${AI_PLACE}/new` : `${AI_PLACE}/new?from=${encodeURIComponent(from)}`
}

/**
 * Settings → Claude logins (S17 §2): where each member connects, renews and disconnects their own
 * Claude logins. Every door of the module about a login — the compose box's hint, the run line, the
 * queue, the plan chip, a reminder's activity row, `/login` — leads here.
 */
export const AI_CLAUDE_LOGIN_HREF = "/settings/claude-logins"
